/**
 * 卸コンソールの枠＝**サイドバー IA 5グループ**（D-42・05 §6.1 が画面のSSoT）。
 *
 * ★ **発注体験は IA に入れない**（D-42）＝あれはデモ専用の演出で、製品の機能ではない。
 * ★ M2/M3 の項目（配送・帳票・請求・在庫）は**出すが非活性**＝
 *   「この製品はここまでやる」を見せつつ、押して空の画面に着地させない（05 §6.1）。
 */

import type { ConsoleMe } from "@kenzai/api-client";
import { Icon, type IconName } from "@kenzai/ui";
import { auth, console_ } from "@kenzai/api-client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { MOBILE_TABBAR_HEIGHT, useIsMobile } from "./useIsMobile";

type Item = { to: string; label: string; phase?: "M2" | "M3"; icon: IconName };

/**
 * testid はルートから作る（`/masters/products` → `nav--masters-products`）。
 *
 * ★**英数字以外はまとめて `-` にする**＝クエリ付きのルート（`/orders?tab=todo`）でも
 *   `nav--orders-tab-todo` になり、E2E から読める名前になる（`?` や `=` をそのまま残すと
 *   セレクタとして扱いにくい）。
 */
function navId(prefix: string, to: string): string {
  return `${prefix}-${to.replace(/[^A-Za-z0-9]+/g, "-")}`;
}
type Group = { title: string; items: Item[] };

// 05 §6.1 の表と 1:1（画面IDはコメントで対応を残す＝デモ ⇄ 設計 ⇄ 本体を追える・D-43）
const GROUPS: Group[] = [
  {
    title: "受注・配送",
    items: [
      { to: "/dashboard", label: "ダッシュボード", icon: "dash" }, // C-01
      // C-04 受注管理＝**旧 C-02 受注箱を統合**（D-76）。メニューは「未処理」を指す＝
      // 卸が1日で最も多く開く形に既定を合わせる（すべては画面のチップで切り替える）。
      { to: "/orders?tab=todo", label: "受注管理", icon: "inbox" },
      // C-21（T-D40・D-69）。**D-77 で「発注管理」に改名**＝社内への発注指示と運営への外注を
      // 束ねる場所。画面ID・route・バッジの定義は変えていない（変えたのは呼び名だけ）。
      { to: "/requests", label: "発注管理", icon: "bag" },
      { to: "/delivery", label: "配送", phase: "M2", icon: "truck" }, // C-05
    ],
  },
  {
    title: "帳票・請求",
    items: [
      { to: "/documents", label: "帳票", phase: "M2", icon: "doc" }, // C-06
      { to: "/billing", label: "請求・締め", phase: "M3", icon: "bill" }, // C-07
    ],
  },
  { title: "在庫", items: [{ to: "/stock", label: "在庫", phase: "M2", icon: "stock" }] }, // C-08
  {
    title: "マスタ",
    items: [
      { to: "/masters/customers", label: "取引先・現場", icon: "building" }, // C-09
      { to: "/masters/products", label: "商品・単価", icon: "box" }, // C-10
      { to: "/masters/bins", label: "配送便", icon: "truck" }, // C-18（便ゼロだと発注が成立しない）
      // C-19 掲載管理は **C-10 のタブ**へ（D-75/D-79）＝サイドバーには出さない
    ],
  },
  {
    title: "設定・管理",
    items: [
      { to: "/settings", label: "会社設定", phase: "M2", icon: "gear" }, // C-11
      { to: "/staff", label: "スタッフと権限", icon: "users" }, // C-12（T-D23）
      // C-13 招待コード・C-20 接続は **C-09 取引先の下**へ（D-79）＝サイドバーには出さない
      { to: "/audit-logs", label: "監査ログ", phase: "M2", icon: "shield" }, // C-14
    ],
  },
];

// スマホの下部タブ＝**外出先の3ジョブ**（D-68・FR-120）＝①受注情報を確認 ②口頭で言われたものを
// 発注処理（手入力）③社内担当者に指示（C-21・S20 で追加）。**IA 全体はドロワーに畳む**＝
// 現場で片手で押すのは3つだけ、という前提を画面の形で示す。
const MOBILE_TABS: Item[] = [
  { to: "/orders?tab=todo", label: "受注管理", icon: "inbox" }, // C-04（旧 受注箱＋受注一覧＝D-76）
  { to: "/orders/new", label: "＋手入力", icon: "clipboard" }, // C-17
  { to: "/requests", label: "発注管理", icon: "bag" }, // C-21（T-D40・D-77 で改名）
];

/**
 * C-21 のバッジ＝**自分宛の未完了**＋**自分が出して未確認の完了**（D-69⑥）。
 *
 * ★ **専用のカウント API を作らない**（05 §4.10）＝一覧と別の数え方が生まれると必ずずれる。
 *   一覧 API が返す `badge_count` をそのまま使う。30秒ポーリング（受注箱と同じ間隔）。
 * ★ 後半（自分が出して未確認の完了）が要るのは、**運営宛の依頼は誰の宛でもない**から＝
 *   「自分宛」だけ数えると、運営が発注を終えても卸側には何も起きない。
 */
function useRequestBadge(): number {
  const q = useQuery({
    queryKey: ["requests-badge"],
    queryFn: () => console_.requests("?limit=1"),
    refetchInterval: 30_000,
  });
  return q.data?.badge_count ?? 0;
}

/**
 * サイドバーの「受注箱」に出す未処理件数（U6・デモの .count ピル）。
 *
 * ★**専用 API を作らない**＝C-01 ダッシュボードが既に返している数（FR-82＝表示ハードコード禁止）
 *   をそのまま使う。別の数え方が生まれると必ずずれる（`useRequestBadge` と同じ考え方）。
 */
function useUnconfirmedCount(): number {
  const q = useQuery({
    // ★ダッシュボードと**同じキー**にする（別キーにすると、承認して一覧が
    //   invalidate されてもピルの数字だけ最大30秒古いまま残り、同じ API を二重に叩く）
    queryKey: ["dashboard"],
    queryFn: () => console_.dashboard(),
    refetchInterval: 30_000,
  });
  return q.data?.unconfirmed ?? 0;
}

export function Shell({ me, onLogout }: { me: ConsoleMe; onLogout: () => void }) {
  const isMobile = useIsMobile();
  const badge = useRequestBadge();
  const unconfirmed = useUnconfirmedCount();
  // ★モバイルとデスクトップを**同時に DOM へ置かない**＝`data-testid` が2つになると
  //   Playwright の strict mode で既存 E2E が 22 箇所落ちる（CSS で隠しても hidden 要素は
  //   strict 判定の対象に数えられるので `display:none` では逃げられない）。
  if (isMobile) return <MobileShell me={me} onLogout={onLogout} badge={badge} />;
  return (
    // デモ（app.css の .app）と同じ骨格＝サイドバー＋**トップバー**＋main の2行グリッド。
    // トップバーが無いと、会社名・ユーザー・パンくずの置き場がサイドバーに押し込まれる（U1 差分2）。
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "var(--kz-sidebar-w) 1fr",
        gridTemplateRows: "var(--kz-topbar-h) 1fr",
        gridTemplateAreas: '"side top" "side main"',
        minHeight: "100dvh",
      }}
    >
      <aside
        style={{
          gridArea: "side",
          // ★地色は `--kz-sidenav-*`（M1.7）＝項目のホバー・選択色は kit.css がこのトークン群から
          //   引いている。ここだけ生のインク色のままだと、基調を変える判断が出たときに揃わない。
          background: "var(--kz-sidenav-bg)",
          color: "var(--kz-sidenav-fg-strong)",
          padding: "var(--kz-space-4) 0",
          display: "flex",
          flexDirection: "column",
          // ★**画面に貼り付ける**（M1.7）＝縦に長いページでスクロールするとメニューが上へ流れて
          //   消えていた。`align-self: start` が要る＝grid の既定 stretch では高さがページ全体に
          //   なり「貼り付く先」が無くなって sticky が効かない。
          position: "sticky",
          top: 0,
          alignSelf: "start",
          height: "100dvh",
        }}
      >
        <div style={{ padding: "0 22px var(--kz-space-4)" }}>
          <div style={{ fontWeight: 700, fontSize: "var(--kz-font-size-lg)" }}>建材オーダー</div>
          <div style={{ fontSize: 11.5, opacity: 0.55 }}>建材卸向け 受発注管理</div>
        </div>

        <nav style={{ flex: 1, overflowY: "auto" }}>
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="kz-nav-group">{g.title}</div>
              {g.items.map((it) =>
                it.phase ? (
                  // **非活性**＝押せない・遷移しない。ツールチップで「いつ」を答える
                  <span
                    key={it.to}
                    className="kz-nav-item is-disabled"
                    title={`${it.phase} で提供予定`}
                    aria-disabled="true"
                    data-testid={navId("nav", it.to)}
                  >
                    <Icon name={it.icon} />
                    {it.label}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        border: "1px solid currentColor",
                        borderRadius: 4,
                        padding: "0 4px",
                      }}
                    >
                      {it.phase}
                    </span>
                  </span>
                ) : (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={navId("nav", it.to)}
                    className={({ isActive }) => (isActive ? "kz-nav-item is-active" : "kz-nav-item")}
                  >
                    <Icon name={it.icon} />
                    {it.label}
                    {/* 件数は**溜まっている物**にだけ出す（全項目に出すと意味が薄れる） */}
                    {/* testid は BadgeDot から引き継ぐ（E2E が「完了が起票者に伝わる」経路を
                        ここで見ている＝D-69⑥。見た目を変えても検証点は動かさない） */}
                    {it.to === "/requests" && badge > 0 && (
                      <span className="kz-nav-count" data-testid="request-badge">
                        {badge}
                      </span>
                    )}
                    {it.to.startsWith("/orders?") && unconfirmed > 0 && (
                      <span className="kz-nav-count" data-testid="nav-count-inbox">
                        {unconfirmed}
                      </span>
                    )}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </nav>
      </aside>

      <header className="kz-topbar" style={{ gridArea: "top" }}>
        {/* テナント文脈が解決していること＝E2E の検証点（Cookie が実際に効いている） */}
        <span
          data-testid="tenant-name"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14.5 }}
        >
          <span style={{ color: "var(--kz-color-primary)", display: "inline-flex" }}>
            <Icon name="building" size={16} />
          </span>
          {me.tenant.name}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          {me.name}（{me.role === "admin" ? "管理者" : "担当者"}）
        </span>
        <button
          type="button"
          data-testid="logout"
          className="kz-btn kz-btn--sm"
          onClick={() => auth.logout().finally(onLogout)}
        >
          ログアウト
        </button>
      </header>

      {/* max-width＝超ワイドで表が間延びしない（デモ .main と同じ 1380px） */}
      <main
        style={{
          gridArea: "main",
          padding: "26px 30px 60px",
          maxWidth: "var(--kz-main-max)",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}

/**
 * スマホ幅の枠＝トップバー（ハンバーガー）＋ main ＋ ボトムバー3タブ（D-68）。
 *
 * ★ z-index は **50 未満**（`ConfirmDialog` が 50）＝ここを 50 以上にすると
 *   「失敗の理由がモーダルの裏に隠れる」（S7 の敵対レビューで直したばかりの事故）を
 *   別の形で再発させる。
 */
function BadgeDot({ count }: { count: number }) {
  return (
    <span
      data-testid="request-badge"
      style={{
        minWidth: 20,
        height: 20,
        padding: "0 6px",
        borderRadius: 999,
        background: "var(--kz-status-danger)",
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* 1ページぶん（100件）を数えた結果なので、到達したら「99+」で丸める（05 §4.10） */}
      {count >= 100 ? "99+" : count}
    </span>
  );
}

function MobileShell({
  me,
  onLogout,
  badge,
}: {
  me: ConsoleMe;
  onLogout: () => void;
  badge: number;
}) {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();

  // ★画面が変わったらドロワーを閉じる（開いたまま遷移すると、行き先の画面が覆われたまま
  //   「押しても何も起きない」に見える）。
  useEffect(() => setDrawer(false), [location.pathname]);

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          gap: "var(--kz-space-3)",
          background: "var(--kz-sidenav-bg)",
          color: "var(--kz-sidenav-fg-strong)",
          padding: "var(--kz-space-2) var(--kz-space-3)",
        }}
      >
        <button
          type="button"
          data-testid="menu-open"
          aria-label="メニューを開く"
          aria-expanded={drawer}
          onClick={() => setDrawer(true)}
          style={{
            minWidth: 44,
            minHeight: 44,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "var(--kz-radius-sm)",
            color: "inherit",
            font: "inherit",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ☰
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>建材オーダー</div>
          <div
            data-testid="tenant-name"
            style={{
              fontSize: "var(--kz-font-size-sm)",
              opacity: 0.75,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {me.tenant.name}
          </div>
        </div>
      </header>

      <main
        style={{
          flex: 1,
          padding: "var(--kz-space-4)",
          minWidth: 0,
          // ★ボトムバーぶんの余白＝足さないと最下部のボタンがタブに隠れて**押せない**
          paddingBottom: MOBILE_TABBAR_HEIGHT + 32,
        }}
      >
        <Outlet />
      </main>

      {drawer && <Drawer me={me} onLogout={onLogout} onClose={() => setDrawer(false)} />}

      <nav
        aria-label="メニュー"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          display: "grid",
          gridTemplateColumns: `repeat(${MOBILE_TABS.length}, 1fr)`,
          background: "var(--kz-color-surface)",
          borderTop: "1px solid var(--kz-color-border)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {MOBILE_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            // パスだけで比べる（`/orders?tab=todo` は end 判定に効かない＝クエリを含むため）。
            // これが無いと /orders/new で「受注管理」と「＋手入力」が同時に現在地になる。
            end={t.to.split("?")[0] === "/orders"}
            data-testid={navId("tab", t.to)}
            style={({ isActive }) => ({
              minHeight: MOBILE_TABBAR_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              color: isActive ? "var(--kz-color-primary)" : "var(--kz-color-ink-muted)",
              fontWeight: isActive ? 700 : 400,
              borderTop: `3px solid ${isActive ? "var(--kz-color-primary)" : "transparent"}`,
              gap: "var(--kz-space-1)",
            })}
          >
            {t.label}
            {t.to === "/requests" && badge > 0 && <BadgeDot count={badge} />}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** ドロワー＝IA の全項目。**サイドバーと同じ `GROUPS` を使い回す**（1つの真実源）。 */
function Drawer({ me, onLogout, onClose }: { me: ConsoleMe; onLogout: () => void; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 30, background: "rgba(0,0,0,0.45)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="navigation"
        aria-label="すべてのメニュー"
        data-testid="drawer"
        style={{
          width: "min(300px, 86vw)",
          height: "100%",
          overflowY: "auto",
          background: "var(--kz-sidenav-bg)",
          color: "var(--kz-sidenav-fg-strong)",
          padding: "var(--kz-space-3) 0",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 var(--kz-space-3)" }}>
          <button
            type="button"
            data-testid="menu-close"
            aria-label="メニューを閉じる"
            onClick={onClose}
            style={{
              minWidth: 44,
              minHeight: 44,
              background: "transparent",
              border: "none",
              color: "inherit",
              font: "inherit",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <nav style={{ flex: 1 }}>
          {GROUPS.map((g) => (
            <div key={g.title} style={{ marginBottom: "var(--kz-space-4)" }}>
              <div
                style={{
                  padding: "0 var(--kz-space-4) var(--kz-space-1)",
                  fontSize: "var(--kz-font-size-sm)",
                  opacity: 0.6,
                  fontWeight: 600,
                }}
              >
                {g.title}
              </div>
              {g.items.map((it) =>
                it.phase ? (
                  <span
                    key={it.to}
                    aria-disabled="true"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--kz-space-2)",
                      minHeight: 44,
                      padding: "0 var(--kz-space-4)",
                      opacity: 0.4,
                    }}
                  >
                    {it.label}
                    <span
                      style={{ fontSize: 11, border: "1px solid currentColor", borderRadius: 4, padding: "0 4px" }}
                    >
                      {it.phase}
                    </span>
                  </span>
                ) : (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={navId("nav", it.to)}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      minHeight: 44,
                      padding: "0 var(--kz-space-4)",
                      color: "inherit",
                      textDecoration: "none",
                      fontWeight: isActive ? 700 : 400,
                      background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                      borderLeft: `3px solid ${isActive ? "var(--kz-color-primary)" : "transparent"}`,
                    })}
                  >
                    {it.label}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </nav>

        <div style={{ padding: "var(--kz-space-4)", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
          <div style={{ fontSize: "var(--kz-font-size-sm)" }}>{me.name}</div>
          <div style={{ fontSize: "var(--kz-font-size-sm)", opacity: 0.6 }}>
            {me.role === "admin" ? "管理者" : "スタッフ"}
          </div>
          <button
            type="button"
            data-testid="logout"
            onClick={() => auth.logout().finally(onLogout)}
            style={{
              marginTop: "var(--kz-space-2)",
              minHeight: 44,
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "var(--kz-radius-sm)",
              color: "inherit",
              font: "inherit",
              padding: "0 var(--kz-space-3)",
              cursor: "pointer",
            }}
          >
            ログアウト
          </button>
        </div>
      </div>
    </div>
  );
}
